"use client";

import { useEffect, useRef } from 'react';
import AppImage from '@/components/ui/AppImage';

const floatingCards = [
{
  id: 'card-1',
  icon: '⚡',
  label: 'Ingeniería Electrónica',
  value: 'Sistemas a medida',
  color: '#07CDE1',
  className: 'float-1 top-[15%] right-[8%] hidden lg:block'
},
{
  id: 'card-2',
  icon: '🔒',
  label: 'Seguridad & Control',
  value: 'CCTV + Acceso',
  color: '#EF4444',
  className: 'float-2 top-[55%] right-[3%] hidden lg:block'
},
{
  id: 'card-3',
  icon: '⚙️',
  label: 'Automatización',
  value: 'Industria 4.0',
  color: '#22C55E',
  className: 'float-3 bottom-[20%] left-[3%] hidden xl:block'
}];


export default function HeroSection() {
  const heroRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleMouseMove = (e: MouseEvent) => {
      if (!heroRef.current) return;
      const { clientX, clientY } = e;
      const { innerWidth, innerHeight } = window;
      const xRatio = (clientX / innerWidth - 0.5) * 20;
      const yRatio = (clientY / innerHeight - 0.5) * 10;
      heroRef.current.style.setProperty('--mouse-x', `${xRatio}px`);
      heroRef.current.style.setProperty('--mouse-y', `${yRatio}px`);
    };

    window.addEventListener('mousemove', handleMouseMove);
    return () => window.removeEventListener('mousemove', handleMouseMove);
  }, []);

  return (
    <section
      ref={heroRef}
      className="relative min-h-screen flex items-center justify-center overflow-hidden hero-gradient circuit-bg"
      id="inicio">
      
      {/* Grid background */}
      <div
        className="absolute inset-0 pointer-events-none"
        style={{
          backgroundImage: 'linear-gradient(rgba(7,205,225,0.03) 1px, transparent 1px), linear-gradient(90deg, rgba(7,205,225,0.03) 1px, transparent 1px)',
          backgroundSize: '60px 60px'
        }} />
      

      {/* Radial glow orbs */}
      <div className="absolute top-1/4 left-1/4 w-96 h-96 rounded-full bg-[rgba(7,205,225,0.05)] blur-3xl pointer-events-none" />
      <div className="absolute bottom-1/4 right-1/4 w-80 h-80 rounded-full bg-[rgba(234,88,12,0.04)] blur-3xl pointer-events-none" />

      {/* Scan line effect */}
      <div
        className="absolute left-0 right-0 h-px pointer-events-none"
        style={{
          background: 'linear-gradient(90deg, transparent, rgba(7,205,225,0.4), transparent)',
          animation: 'scanLine 6s linear infinite'
        }} />
      

      {/* Floating cards */}
      {floatingCards.map((card) =>
      <div
        key={card.id}
        className={`absolute glass-card rounded-lg p-4 min-w-[180px] ${card.className}`}
        style={{ borderColor: `${card.color}30` }}>
        
          <div className="flex items-center gap-3">
            <div
            className="w-10 h-10 rounded-md flex items-center justify-center text-lg flex-shrink-0"
            style={{ background: `${card.color}15`, border: `1px solid ${card.color}40` }}>
            
              {card.icon}
            </div>
            <div>
              <p className="text-[10px] font-body tracking-wider uppercase" style={{ color: card.color }}>
                {card.label}
              </p>
              <p className="text-white font-heading font-600 text-sm mt-0.5">{card.value}</p>
            </div>
          </div>
          <div
          className="mt-3 h-1 rounded-full"
          style={{ background: `linear-gradient(90deg, ${card.color}, transparent)` }} />
        
        </div>
      )}

      {/* Main content */}
      <div className="relative z-10 max-w-7xl mx-auto px-6 md:px-12 pt-32 pb-20 grid grid-cols-1 lg:grid-cols-12 gap-12 items-center">
        {/* Left content */}
        <div className="lg:col-span-7">
          {/* Badge */}
          <div className="inline-flex items-center gap-2 tech-badge mb-8">
            <span className="w-2 h-2 rounded-full bg-[#07CDE1] animate-pulse" />
            Barquisimeto, Lara · Venezuela · Desde 2013
          </div>

          {/* Headline */}
          <h1 className="font-heading font-800 leading-[0.9] tracking-tight mb-6">
            <span className="block text-white text-5xl md:text-7xl lg:text-[5.5rem] xl:text-[6rem]">
              Soluciones
            </span>
            <span
              className="block text-5xl md:text-7xl lg:text-[5.5rem] xl:text-[6rem] gradient-text-cyan">
              
              Tecnológicas
            </span>
            <span className="block text-white text-5xl md:text-7xl lg:text-[5.5rem] xl:text-[6rem]">
              a{' '}
              <span className="orange-underline gradient-text-orange">
                Medida
              </span>
            </span>
          </h1>

          {/* Subheadline */}
          <p className="font-body text-[#8892A4] text-lg md:text-xl leading-relaxed max-w-xl mt-8 mb-10">
            Transformamos ideas en soluciones digitales y tecnológicas tangibles.
            Desde ingeniería electrónica hasta automatización industrial —
            tu socio tecnológico integral en Venezuela.
          </p>

          {/* CTAs */}
          <div className="flex flex-col sm:flex-row gap-4">
            <a
              href="#contacto"
              className="btn-primary inline-flex items-center justify-center gap-3 px-8 py-4 rounded text-base">
              
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" />
              </svg>
              ¿Trabajamos juntos?
            </a>
            <a
              href="#servicios"
              className="btn-secondary inline-flex items-center justify-center gap-3 px-8 py-4 rounded text-base">
              
              Ver servicios
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
              </svg>
            </a>
          </div>

          {/* Tech chips */}
          <div className="flex flex-wrap gap-2 mt-10">
            {['Electrónica', 'Software', 'Automatización', 'CCTV', 'Redes', 'Impresión 3D'].map((tag) =>
            <span
              key={tag}
              className="text-[11px] font-body font-500 px-3 py-1 rounded-full text-[#8892A4]"
              style={{
                background: 'rgba(136,146,164,0.08)',
                border: '1px solid rgba(136,146,164,0.15)'
              }}>
              
                {tag}
              </span>
            )}
          </div>
        </div>

        {/* Right: Stats panel */}
        <div className="lg:col-span-5 flex flex-col gap-4">
          {/* Main image card */}
          <div className="relative glass-card rounded-xl overflow-hidden h-64 scan-effect">
            <AppImage
              src="https://images.unsplash.com/photo-1613500427726-342d2b35d7ee"
              alt="Circuit board close-up showing electronic components and copper traces — GTS Consultores engineering lab"
              fill
              className="object-cover opacity-60" />
            
            <div className="absolute inset-0 bg-gradient-to-t from-[#0A0F1A] via-transparent to-transparent" />
            <div className="absolute bottom-4 left-4 right-4">
              <p className="text-[10px] font-body tracking-widest uppercase text-[#07CDE1] mb-1">
                // Ingeniería de precisión
              </p>
              <p className="text-white font-heading font-700 text-lg">
                Laboratorio de Electrónica
              </p>
            </div>
          </div>

          {/* Stats row */}
          <div className="grid grid-cols-3 gap-3">
            {[
            { value: '7+', label: 'Unidades de negocio' },
            { value: '50+', label: 'Proyectos entregados' },
            { value: '100%', label: 'Soluciones a medida' }].
            map((stat) =>
            <div
              key={stat.label}
              className="glass-card rounded-lg p-4 text-center">
              
                <p className="stat-number text-2xl font-800 font-heading">{stat.value}</p>
                <p className="text-[10px] font-body text-[#8892A4] mt-1 leading-tight">{stat.label}</p>
              </div>
            )}
          </div>

          {/* Contact quick-access */}
          <div className="glass-card rounded-lg p-4 flex items-center gap-4">
            <div className="w-10 h-10 rounded-full bg-[#25D366]/15 border border-[#25D366]/30 flex items-center justify-center flex-shrink-0">
              <svg viewBox="0 0 24 24" className="w-5 h-5 fill-[#25D366]">
                <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z" />
              </svg>
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-[10px] font-body text-[#8892A4] uppercase tracking-wider">Contáctanos ahora</p>
              <a
                href="https://wa.me/584245756868"
                target="_blank"
                rel="noopener noreferrer"
                className="text-white font-heading font-600 text-sm hover:text-[#25D366] transition-colors truncate block">
                
                +58 424 575 6868
              </a>
            </div>
            <a
              href="https://wa.me/582514181160"
              target="_blank"
              rel="noopener noreferrer"
              className="text-[11px] font-body text-[#8892A4] hover:text-white transition-colors flex-shrink-0">
              
              +58 251 418 1160
            </a>
          </div>
        </div>
      </div>

      {/* Scroll indicator */}
      <div className="absolute bottom-8 left-1/2 -translate-x-1/2 flex flex-col items-center gap-2 opacity-40">
        <span className="text-[10px] font-body tracking-widest uppercase text-[#07CDE1]">Scroll</span>
        <div className="w-px h-10 bg-gradient-to-b from-[#07CDE1] to-transparent" />
      </div>
    </section>);

}